
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Review {
  id: string;
  user_id: string;
  partner_id: string;
  partner_name: string;
  rating: number;
  comment: string;
  user_name: string;
  created_at: string;
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('리뷰 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteReview = async (reviewId: string) => {
    if (!confirm('리뷰를 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId);

      if (error) throw error;
      
      setReviews(reviews.filter(review => review.id !== reviewId));
    } catch (error) {
      console.error('리뷰 삭제 오류:', error);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}
      ></i>
    ));
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === 'all') return true;
    if (filter === 'high') return review.rating >= 4;
    if (filter === 'low') return review.rating <= 2;
    return review.partner_name === filter;
  });

  const partnerNames = [...new Set(reviews.map(review => review.partner_name))];

  if (loading) {
    return (
      <div className="p-8 text-center">
        <i className="ri-loader-4-line text-2xl text-emerald-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">리뷰 관리</h2>
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 pr-8"
          >
            <option value="all">전체</option>
            <option value="high">높은 평점 (4점 이상)</option>
            <option value="low">낮은 평점 (2점 이하)</option>
            {partnerNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <span className="text-sm text-gray-600">
            총 {filteredReviews.length}개 리뷰
          </span>
        </div>
      </div>

      {filteredReviews.length === 0 ? (
        <div className="text-center py-12">
          <i className="ri-star-line text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-500 text-lg">리뷰가 없습니다.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => (
            <div key={review.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-4">
                      <span className="font-semibold text-gray-900">{review.user_name}</span>
                      <div className="flex items-center space-x-1">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-600 ml-2">({review.rating}점)</span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteReview(review.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="삭제"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                  
                  <div className="mb-3">
                    <span className="inline-block bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full text-sm">
                      {review.partner_name}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-3">{review.comment}</p>
                  
                  <div className="text-sm text-gray-500">
                    {new Date(review.created_at).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
